import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
// import { useBreadcrumbs } from '@/hooks/navigation/useBreadcrumbs';

// Temporary placeholder for missing hook
const useBreadcrumbs = () => ({ breadcrumbs: [] as any[] });

/**
 * Accessible navigation component with ARIA landmarks and screen reader support
 */
export const AccessibleNavigation: React.FC<{
  children: React.ReactNode;
  skipToContentId?: string;
  announcePageChanges?: boolean;
}> = ({
  children,
  skipToContentId = "main-content",
  announcePageChanges = true,
}) => {
  const location = useLocation();
  const { breadcrumbs } = useBreadcrumbs();
  const announceRef = useRef<HTMLDivElement>(null);

  /**
   * Announce page changes to screen readers
   */
  useEffect(() => {
    if (!announcePageChanges || !announceRef.current) return;

    const currentPage = breadcrumbs.find((crumb) => crumb.isCurrentPage);
    if (currentPage) {
      const announcement = `Navigated to ${currentPage.label}`;
      announceRef.current.textContent = announcement;

      // Clear after announcement
      setTimeout(() => {
        if (announceRef.current) {
          announceRef.current.textContent = "";
        }
      }, 1000);
    }
  }, [location.pathname, breadcrumbs, announcePageChanges]);

  return (
    <>
      {/* Skip to main content link */}
      <SkipLink targetId={skipToContentId} />

      {/* Screen reader announcements */}
      <div
        ref={announceRef}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        role="status"
      />

      {/* Main navigation wrapper */}
      <div className="navigation-wrapper">{children}</div>
    </>
  );
};

/**
 * Skip to main content link for keyboard users
 */
const SkipLink: React.FC<{ targetId: string }> = ({ targetId }) => {
  const handleSkip = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleSkip}
      className="skip-link absolute top-0 left-0 z-50 p-2 bg-primary text-primary-foreground
                 font-medium focus:translate-y-0 -translate-y-full transition-transform
                 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      onFocus={(e) => e.currentTarget.classList.add("translate-y-0")}
      onBlur={(e) => e.currentTarget.classList.remove("translate-y-0")}
    >
      Skip to main content
    </a>
  );
};

/**
 * Navigation landmark component with proper ARIA roles
 */
export const NavigationLandmark: React.FC<{
  children: React.ReactNode;
  label: string;
  description?: string;
}> = ({ children, label, description }) => {
  return (
    <nav
      role="navigation"
      aria-label={label}
      aria-describedby={
        description
          ? `${label.toLowerCase().replace(/\s+/g, "-")}-desc`
          : undefined
      }
    >
      {description && (
        <div
          id={`${label.toLowerCase().replace(/\s+/g, "-")}-desc`}
          className="sr-only"
        >
          {description}
        </div>
      )}
      {children}
    </nav>
  );
};

/**
 * Main content landmark with proper focus management
 */
export const MainContentLandmark: React.FC<{
  children: React.ReactNode;
  id?: string;
}> = ({ children, id = "main-content" }) => {
  const mainRef = useRef<HTMLElement>(null);

  // Ensure main content is focusable for skip link
  useEffect(() => {
    if (mainRef.current && !mainRef.current.hasAttribute("tabindex")) {
      mainRef.current.setAttribute("tabindex", "-1");
    }
  }, []);

  return (
    <main
      ref={mainRef}
      id={id}
      role="main"
      aria-label="Main content"
      className="h-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
    >
      {children}
    </main>
  );
};

/**
 * Complementary content landmark (sidebars, etc.)
 */
export const ComplementaryLandmark: React.FC<{
  children: React.ReactNode;
  label: string;
}> = ({ children, label }) => {
  return (
    <aside role="complementary" aria-label={label}>
      {children}
    </aside>
  );
};

/**
 * Search landmark for search functionality
 */
export const SearchLandmark: React.FC<{
  children: React.ReactNode;
  label?: string;
}> = ({ children, label = "Search" }) => {
  return (
    <div role="search" aria-label={label}>
      {children}
    </div>
  );
};

/**
 * Region landmark for content sections
 */
export const RegionLandmark: React.FC<{
  children: React.ReactNode;
  label: string;
}> = ({ children, label }) => {
  return (
    <section role="region" aria-label={label}>
      {children}
    </section>
  );
};

/**
 * Enhanced focus trap component for modals and dropdowns
 */
export const FocusTrap: React.FC<{
  children: React.ReactNode;
  active: boolean;
  restoreFocus?: boolean;
  autoFocus?: boolean;
}> = ({ children, active, restoreFocus = true, autoFocus = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    // Store previous focus
    if (restoreFocus) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }

    // Get focusable elements
    const getFocusableElements = (): HTMLElement[] => {
      if (!containerRef.current) return [];

      const focusableSelectors = [
        "button:not([disabled])",
        "input:not([disabled])",
        "textarea:not([disabled])",
        "select:not([disabled])",
        "a[href]",
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
      ].join(", ");

      return Array.from(
        containerRef.current.querySelectorAll(focusableSelectors),
      );
    };

    // Focus first element if autoFocus is enabled
    if (autoFocus) {
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    // Handle tab navigation
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (event.shiftKey) {
        // Shift + Tab
        if (activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      // Restore previous focus
      if (restoreFocus && previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [active, autoFocus, restoreFocus]);

  return <div ref={containerRef}>{children}</div>;
};

/**
 * Reduced motion wrapper that respects user preferences
 */
export const ReducedMotionWrapper: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  if (prefersReducedMotion && fallback) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
